// Click panel (design D5, atlas-feature-info spec): what the visible atlas layers say at a point.
// Client-side only; nothing about the click leaves the browser.

import { h, link, provenance } from "./dom";
import type { StyledLayer } from "./manifest";
import type { Field } from "./styles/types";

export interface ClickedFeature {
  layerId: string; // atlas layer id (the source-layer name)
  properties: Record<string, unknown>;
}

export interface PanelItem {
  layer: StyledLayer;
  fields: Field[];
}

// Never shown, whatever a style module says (hard rule: owner names never leave the fetch).
const BLOCKED = /owner/i;

/**
 * One item per distinct described feature. Fields come only from the layer's `describe()`, so
 * an attribute the style module doesn't list never shows; owner fields are dropped on top.
 */
export function describeFeatures(features: ClickedFeature[], layers: StyledLayer[]): PanelItem[] {
  const byId = new Map(layers.map((l) => [l.entry.id, l]));
  const items: PanelItem[] = [];
  const seen = new Set<string>();
  for (const f of features) {
    const layer = byId.get(f.layerId);
    if (!layer) continue;
    const fields = layer.style.describe(f.properties).filter((x) => !BLOCKED.test(x.key) && !BLOCKED.test(x.label));
    if (!fields.length) continue;
    // A polygon split across tiles comes back once per tile.
    const key = `${f.layerId}|${fields.map((x) => `${x.key}=${x.value}`).join("|")}`;
    if (seen.has(key)) continue;
    seen.add(key);
    items.push({ layer, fields });
  }
  return items;
}

export function renderPanel(container: HTMLElement, items: PanelItem[], onClose: () => void): void {
  const close = h("button", { type: "button", class: "close", "aria-label": "Close" }, "×");
  close.addEventListener("click", onClose);
  const body: Node[] = [];
  if (!items.length) {
    body.push(h("p", { class: "empty" }, "No visible layer has data at this point."));
  }
  for (const { layer, fields } of items) {
    const dl = h("dl");
    for (const f of fields) dl.append(h("dt", {}, f.label), h("dd", {}, f.value));
    const section = h("section", { class: "item", "data-layer": layer.entry.id }, h("h3", {}, layer.entry.title), dl, provenance(layer.entry));
    if (layer.style.links?.length) section.append(h("p", { class: "official" }, ...layer.style.links.flatMap((l, i) => [i ? " · " : "", link(l.href, l.label)])));
    body.push(section);
  }
  container.replaceChildren(h("header", {}, h("h2", {}, "At this point"), close), h("p", { class: "note" }, "Visible layers at this zoom."), ...body);
  container.hidden = false;
}
